'use client';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useTranslations } from 'next-intl';

import Loading from '../../../components/Loading';
import LoadingContainer from '../../../components/LoadingContainer';
import ResourceItem from '../../../components/ResourceItem';
import Wrapper from '../../../components/Wrapper';
import { amplitudeInstance } from '../../../config/amplitude';
import AuthContext from '../../../context/AuthContext';
import buildRouteUrl from '../../../helpers/buildRouteUrl';
import useIntl from '../../../hooks/useIntl';
import { Link } from '../../../i18n/navigation';
import api from '../../../services/api';
import {
  BasicResource,
  Metadata,
} from '../../../types/types';
import buildTitleUrl from '../../../helpers/buildTitleUrl';

function ClientPopular() {
  const intl = useIntl();
  const auth = useContext(AuthContext);

  const t = useTranslations();

  const [totalTitles, setTotalTitles] = useState(0);

  const containerRef = useRef<any>(null);

  const popularTitlesQuery = useInfiniteQuery<{
    data: BasicResource[];
    metadata: Metadata;
  }>({
      queryKey:  [
        `popular-titles`,
        intl.locale
      ],
      queryFn: async ({ pageParam: offset = 0 }) => {
        const response = await api.get(
          `/v1/popular/titles/?offset=${
            offset || 0
          }&limit=50`,
          {
            headers: {
              'accept-language': intl.full_language
            },
            params: {
              region: intl.locale
            }
          }
        );

        return response.data;
      },
      initialPageParam: 0,
      getNextPageParam: lastGroup => {
        return false;
      }
    }
  );

  useEffect(() => {
    if (popularTitlesQuery.isSuccess) {
      // eslint-disable-next-line no-unused-expressions
      popularTitlesQuery.data?.pages.forEach(group => {
        setTotalTitles(prevState => prevState + group.data.length);
      });
    }
  }, [popularTitlesQuery.isSuccess, popularTitlesQuery.data]);


  return (
    <>
      <Wrapper>
        <BottomScrollListener onBottom={() => {}} offset={150}>
          {() => (
            <div
              ref={containerRef}
              className="flex flex-col w-full bg-gray-600 px-4 md:px-10"
            >
              <div className="flex flex-col bg-gray-600 mt-3 pb-1 w-full z-[8]">
                <div>
                  <h1 className="text-white text-3xl font-bold">
                    {t('popular.title', {
                      region: t(
                        `countries.${
                          auth.userData?.content_region ||
                          intl.locale.toUpperCase()
                        }`
                      )
                    })}
                  </h1>
                  <p className="text-gray-500 mt-1">
                    {t('popular.description', {
                      region: t(
                        `countries.${
                          auth.userData?.content_region ||
                          intl.locale.toUpperCase()
                        }`
                      )
                    })}
                  </p>
                </div>
              </div>

              {popularTitlesQuery.isLoading && (
                <LoadingContainer>
                  <Loading />
                </LoadingContainer>
              )}

              {/* {popularTitlesQuery.isSuccess &&
                popularTitlesQuery.data.pages[0].metadata.total === 0 && (
                  <WarningContainer>
                    <WarningText>
                      {t('watch_providers_titles.warnings.title_not_found')}
                    </WarningText>
                  </WarningContainer>
                )} */}

              {popularTitlesQuery.isSuccess && totalTitles > 0 && (
                <div className="custom-grid w-full h-full gap-x-6 gap-y-3 pb-4 mt-4">
                  {popularTitlesQuery.data?.pages.map(
                    (group, groupIndex) => (
                      <React.Fragment key={groupIndex}>
                        {group.data.map(item => {
                          return (
                            <Link
                              key={item._id}
                              href={buildTitleUrl({
                                type: item.type,
                                locale: intl.locale.toLowerCase(),
                                shortId: item.short_id,
                                slugs: item.slugs,
                              })}
                              prefetch={false}
                            >
                              <ResourceItem
                                key={item._id}
                                data={item}
                                onClick={() => {
                                  amplitudeInstance.logEvent(
                                    'Go To Content Page From Popular Titles Page',
                                    {
                                      content_id: item._id,
                                      content_type: item.type,
                                    }
                                  );
                                }}
                              />
                            </Link>
                          );
                        })}
                      </React.Fragment>
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </BottomScrollListener>
      </Wrapper>
    </>
  );
}

export default ClientPopular;
